/* eslint frontbucket-patterns/no-new-sagas: "warn" */
import { call, select, take, cancel, fork, delay } from 'redux-saga/effects';

import { User } from 'src/components/types';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { getCurrentWorkspaceUUID } from 'src/selectors/workspace-selectors';
import { userWorkspacePreferences } from 'src/utils/scoped-preferences';

import { TOGGLE_SIDEBAR, ToggleSidebarAction } from '../actions';
import { isSidebarOpen, getSidebarWidth } from '../reducer';

export function* onToggleSidebar(action: ToggleSidebarAction) {
  const { sidebarType, skipUpdatePref } = action.payload;

  const currentUser: User = yield select(getCurrentUser);
  const isOpen: boolean = yield select(isSidebarOpen, sidebarType);
  const width: number = yield select(getSidebarWidth, sidebarType);

  if (currentUser && !skipUpdatePref) {
    const workspaceUuid: string = yield select(getCurrentWorkspaceUUID);
    if (!workspaceUuid) {
      return;
    }
    yield call(
      userWorkspacePreferences.set,
      currentUser.uuid,
      workspaceUuid,
      `right-sidebar-${sidebarType}-collapsed`,
      !isOpen
    );
    if (isOpen) {
      yield call(
        userWorkspacePreferences.set,
        currentUser.uuid,
        workspaceUuid,
        `right-sidebar-${sidebarType}-width`,
        width
      );
    }
  }
}

export function* toggleSidebarSaga() {
  let skipUpdatePref = false;

  // Debouncing the saga reduces load on the preferences API when rapidly toggling the sidebar
  // It also limits this saga to running once per toggle (the TOGGLE_SIDEBAR action can fire
  // twice if the sidebar collapsed/expanded state is triggered externally: once to open/close
  // and once via the sidebar onResize callback to update redux to the new width)
  // Taken from redux-saga-debounce-effect library, copied because we've bumped to newer redux-saga
  // @ts-ignore TODO: fix noImplicitAny error here
  function* delayedSaga(action) {
    yield delay(500);
    // Reset this value now that the debounce period is over
    skipUpdatePref = false;
    yield call(onToggleSidebar, action);
  }

  let task;
  while (true) {
    // @ts-ignore
    const action = yield take(TOGGLE_SIDEBAR);
    if (task) {
      yield cancel(task);
    }

    /**
     * If any 1 sidebar toggle action within a debounce period is configured
     * to skip updating the user preference, we should ensure that carries through
     * to the final action that eventually resolves
     *
     * Without this, the preference API is always called because TOGGLE_SIDEBAR usually
     * (always?) fires at least a 2nd time due to the sidebar's `onResize` callback that
     * fires when the sidebar expands or collapses and that handles persisting the new width.
     */
    skipUpdatePref = action.payload.skipUpdatePref || skipUpdatePref;
    if (skipUpdatePref) {
      // @ts-ignore
      task = yield fork(delayedSaga, {
        ...action,
        payload: { ...action.payload, skipUpdatePref },
      });
    } else {
      // @ts-ignore
      task = yield fork(delayedSaga, action);
    }
  }
}
